import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Send } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { placementOptions } from "../mock";
import api from "../lib/api";

const emptyForm = {
  fullName: "", email: "", company: "", website: "", position: "",
  phone: "", employees: "", people: "", address: "",
  visitDate: "", visitTime: "", placement: "", notes: ""
};

export default function BookingForm({ compact = false, source = "booking" }) {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = ["fullName", "email", "company", "position", "phone", "employees", "people", "address", "visitDate", "visitTime", "placement"];
    const missing = required.filter((k) => !String(form[k]).trim());
    if (missing.length) {
      toast({ title: "A few fields need attention", description: "Please complete all required (*) fields.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/submissions", { ...form, source });
      setForm(emptyForm);
      // Redirect to the Thank You page — the conversion is measured there
      // via a page view (see src/pages/ThankYou.jsx).
      navigate("/thank-you", { state: { name: form.fullName, source } });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again, or call us directly.", variant: "destructive" });
      setSubmitting(false);
    }
  };

  const fieldCls = "h-11 rounded-lg border-gold/30 focus-visible:ring-gold bg-white";

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className={`grid gap-4 ${compact ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 md:grid-cols-2"}`}>
        <div className="space-y-1.5">
          <Label>Full Name <span className="text-gold">*</span></Label>
          <Input value={form.fullName} onChange={set("fullName")} placeholder="e.g. Jane Doe" className={fieldCls} />
        </div>
        <div className="space-y-1.5">
          <Label>Email <span className="text-gold">*</span></Label>
          <Input type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={fieldCls} />
        </div>
        <div className="space-y-1.5">
          <Label>Company Name <span className="text-gold">*</span></Label>
          <Input value={form.company} onChange={set("company")} placeholder="Company / business name" className={fieldCls} />
        </div>
        <div className="space-y-1.5">
          <Label>Website / Social Link</Label>
          <Input value={form.website} onChange={set("website")} placeholder="e.g. example.com" className={fieldCls} />
        </div>
        <div className="space-y-1.5">
          <Label>Position / Title <span className="text-gold">*</span></Label>
          <Input value={form.position} onChange={set("position")} placeholder="e.g. Office Manager" className={fieldCls} />
        </div>
        <div className="space-y-1.5">
          <Label>Phone Number <span className="text-gold">*</span></Label>
          <Input value={form.phone} onChange={set("phone")} placeholder="04xx xxx xxx" className={fieldCls} />
        </div>
        <div className="space-y-1.5">
          <Label>Employees / Foot traffic (daily) <span className="text-gold">*</span></Label>
          <Input value={form.employees} onChange={set("employees")} placeholder="e.g. 50" className={fieldCls} />
        </div>
        <div className="space-y-1.5">
          <Label>People on Premises <span className="text-gold">*</span></Label>
          <Input value={form.people} onChange={set("people")} placeholder="e.g. 45" className={fieldCls} />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Company Site Address <span className="text-gold">*</span></Label>
        <Input value={form.address} onChange={set("address")} placeholder="Street, suburb, postcode" className={fieldCls} />
      </div>

      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Preferred Site Visit Date <span className="text-gold">*</span></Label>
          <Input type="date" value={form.visitDate} onChange={set("visitDate")} className={fieldCls} />
        </div>
        <div className="space-y-1.5">
          <Label>Preferred Site Visit Time <span className="text-gold">*</span></Label>
          <Input type="time" value={form.visitTime} onChange={set("visitTime")} className={fieldCls} />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Where would you like the machine to go? <span className="text-gold">*</span></Label>
        <Select value={form.placement} onValueChange={set("placement")}>
          <SelectTrigger className={fieldCls}>
            <SelectValue placeholder="Select an option" />
          </SelectTrigger>
          <SelectContent>
            {placementOptions.map((o) => (
              <SelectItem key={o} value={o}>{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label>Additional Notes / Questions</Label>
        <Textarea value={form.notes} onChange={set("notes")} placeholder="Anything else we should know?" className="rounded-lg border-gold/30 focus-visible:ring-gold bg-white min-h-[90px]" />
      </div>

      <Button
        type="submit"
        disabled={submitting}
        className="w-full h-12 bg-ink hover:bg-gold hover:text-ink text-white font-semibold rounded-full transition-colors group"
      >
        {submitting ? "Sending…" : (<span className="flex items-center gap-2"><Send size={17} /> Submit Site Visit Request</span>)}
      </Button>

      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <ShieldCheck size={14} className="text-gold" />
        By submitting, you agree to be contacted about your site visit booking. No lock-in contracts.
      </p>
    </form>
  );
}
